import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import "./modal.css";

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = "md",
  closeOnEsc = true,
  closeOnBackdrop = true,
  showCloseButton = true,
  ariaLabel = "Dialog",
}) {
  const modalRef = useRef(null);
  const previouslyFocused = useRef(null);

  // Read the latest onClose/closeOnEsc from refs inside the focus effect so
  // callers can pass inline handlers without retriggering it. Keying the effect
  // on those props re-ran focus save/restore on every parent re-render, which
  // ping-ponged focus between the background trigger and the modal's close
  // button (a visible flicker).
  const onCloseRef = useRef(onClose);
  const closeOnEscRef = useRef(closeOnEsc);
  useEffect(() => {
    onCloseRef.current = onClose;
    closeOnEscRef.current = closeOnEsc;
  });

  useEffect(() => {
    if (!isOpen) return;

    // Save previous focus
    previouslyFocused.current = document.activeElement;
    // Lock scroll
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Focus the modal
    const focusTimer = setTimeout(() => {
      const focusable = getFocusable(modalRef.current);
      if (focusable.length) focusable[0].focus();
      else modalRef.current?.focus();
    }, 0);

    const handleKey = (e) => {
      if (e.key === "Escape" && closeOnEscRef.current) {
        e.stopPropagation();
        onCloseRef.current?.();
      } else if (e.key === "Tab") {
        trapTabKey(modalRef.current, e);
      }
    };

    document.addEventListener("keydown", handleKey);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = originalOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (!closeOnBackdrop) return;
    if (e.target === e.currentTarget) onClose?.();
  };

  // Render modal in portal
  return ReactDOM.createPortal(
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={handleBackdropClick}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={title ? "modal-title" : undefined}
        tabIndex={-1}
        className={`modal ${size}`}
        ref={modalRef}
      >
        <div className="modal-inner">
          {showCloseButton && (
            <button
              type="button"
              aria-label="Close"
              className="modal-close-btn"
              onClick={() => onClose?.()}
            >
              ×
            </button>
          )}

          {title && (
            <header className="modal-header">
              <h2 id="modal-title">{title}</h2>
            </header>
          )}

          <div className="modal-body">{children}</div>

          {footer !== undefined && <footer className="modal-footer">{footer}</footer>}
        </div>
      </div>
    </div>,
    document.body // portal root
  );
}

/* Helpers */
function getFocusable(container) {
  if (!container) return [];
  return Array.from(
    container.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((el) => el.offsetWidth || el.offsetHeight || el.getClientRects().length);
}

function trapTabKey(container, e) {
  const focusable = getFocusable(container);
  if (!focusable.length) {
    e.preventDefault();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const isShift = e.shiftKey;

  if (isShift && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!isShift && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

/* PropTypes */
Modal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node,
  footer: PropTypes.node,
  size: PropTypes.oneOf(["sm", "md", "lg", "full"]),
  closeOnEsc: PropTypes.bool,
  closeOnBackdrop: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  ariaLabel: PropTypes.string,
};
